import { useEffect, useState } from "react";
import { getAdminStats, getUtmStats } from "@/lib/checkout";
import { Users, ShoppingCart, DollarSign, Mail, RefreshCw, Target } from "lucide-react";
import { AdminGate } from "@/components/AdminGate";
import { useNoIndex } from "@/hooks/useNoIndex";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const fmtDay = (iso) => {
  const parts = (iso || "").split("-");
  return parts.length === 3 ? `${parts[1]}/${parts[2]}` : iso;
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-[#1E2A47] bg-[#111A30] p-6" data-testid={testid}>
    <div className="flex items-center gap-2 text-[#7BA8C4]">
      <Icon className="h-4 w-4" />
      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
    </div>
    <p className="mt-3 font-heading text-3xl font-extrabold text-white">{value}</p>
  </div>
);

export default function AdminStats() {
  useNoIndex();
  const [key, setKey] = useState("");
  const [entered, setEntered] = useState(() => localStorage.getItem("cc_admin_key") || "");
  const [data, setData] = useState(null);
  const [utm, setUtm] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | error | ready
  const [error, setError] = useState("");
  const [range, setRange] = useState(30);

  const load = async (k, days = range) => {
    setStatus("loading");
    setError("");
    try {
      const [stats, utmStats] = await Promise.all([
        getAdminStats(k, days),
        getUtmStats(k, days),
      ]);
      setData(stats);
      setUtm(utmStats);
      setStatus("ready");
      localStorage.setItem("cc_admin_key", k);
    } catch (e) {
      setStatus("error");
      setError(e.message === "unauthorized" ? "That key is not valid." : "Could not load stats. Try again.");
      if (e.message === "unauthorized") localStorage.removeItem("cc_admin_key");
    }
  };

  const changeRange = (days) => {
    setRange(days);
    if (entered) load(entered, days);
  };

  useEffect(() => {
    if (entered) load(entered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setEntered(key);
    load(key);
  };

  if (status !== "ready") {
    return (
      <AdminGate
        title="Private stats"
        subtitle="Enter your admin key to open the dashboard."
        value={key}
        onChange={(e) => setKey(e.target.value)}
        onSubmit={submit}
        loading={status === "loading"}
        error={error}
        gateTestId="admin-gate"
        inputTestId="admin-key-input"
        errorTestId="admin-error"
        buttonTestId="admin-unlock-button"
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#0B132B] px-6 py-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[900px]">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-3xl font-extrabold text-white">CommandCenterADHD stats</h1>
          <button
            type="button"
            onClick={() => load(entered)}
            data-testid="admin-refresh-button"
            className="inline-flex items-center gap-2 rounded-xl border border-[#2A3A5E] px-4 py-2 text-sm font-semibold text-[#A8D5F5] hover:bg-white/5"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={Users} label="Free signups" value={data.total_signups} testid="stat-signups" />
          <StatCard icon={ShoppingCart} label="Paid orders" value={data.total_orders} testid="stat-orders" />
          <StatCard icon={DollarSign} label="Revenue" value={`$${data.revenue_usd}`} testid="stat-revenue" />
          <StatCard icon={Mail} label="Nudges sent" value={data.nudges_sent} testid="stat-nudges" />
        </div>

        <div className="mt-10 rounded-2xl border border-[#1E2A47] bg-[#111A30] p-6" data-testid="utm-table">
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-[#F5A623]" />
            <h2 className="font-heading text-lg font-extrabold text-white">
              Traffic sources and purchase conversion
            </h2>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-[#7BA8C4]">
            Checkout start to purchase by UTM source, medium, and campaign, over the last {utm && utm.range_days === "all" ? "all" : (utm ? utm.range_days : range)} days. This counts people who reached the Stripe or PayPal checkout, not every visit. Visit level traffic lives in your GA4 reports.
          </p>

          {!utm || !utm.rows || utm.rows.length === 0 ? (
            <p className="mt-5 rounded-xl border border-dashed border-[#2A3A5E] bg-[#0B132B] p-5 text-sm text-[#A8D5F5]" data-testid="utm-empty">
              No checkout activity in this window yet. Once visitors start reaching checkout from your ads, their source, medium, and campaign will appear here.
            </p>
          ) : (
            <>
              {!utm.has_utm_data && (
                <p className="mt-4 rounded-xl border border-[#F5A623]/30 bg-[#F5A623]/10 p-4 text-sm text-[#F5D79A]" data-testid="utm-untagged-note">
                  Heads up: none of the checkouts in this window carried UTM tags yet, so everything is grouped under direct. Add utm_source, utm_medium, and utm_campaign to your ad links, then give it about a week of tagged traffic for this table to tell you which channels convert.
                </p>
              )}
              <div className="mt-4 overflow-x-auto rounded-xl border border-[#1E2A47]">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#0B132B] text-[#7BA8C4]">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Source</th>
                      <th className="px-4 py-3 font-semibold">Medium</th>
                      <th className="px-4 py-3 font-semibold">Campaign</th>
                      <th className="px-4 py-3 font-semibold">Checkouts</th>
                      <th className="px-4 py-3 font-semibold">Purchases</th>
                      <th className="px-4 py-3 font-semibold">Conv rate</th>
                      <th className="px-4 py-3 font-semibold">Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#1E2A47] text-white">
                    {utm.rows.map((r, i) => (
                      <tr key={`${r.source}-${r.medium}-${r.campaign}-${i}`} data-testid={`utm-row-${i}`}>
                        <td className="px-4 py-3 font-heading font-bold">{r.source}</td>
                        <td className="px-4 py-3 text-[#A8D5F5]">{r.medium}</td>
                        <td className="px-4 py-3 text-[#A8D5F5]">{r.campaign}</td>
                        <td className="px-4 py-3">{r.starts}</td>
                        <td className="px-4 py-3">{r.purchases}</td>
                        <td className="px-4 py-3 font-semibold text-[#4FD1B5]">{(r.conversion_rate * 100).toFixed(1)}%</td>
                        <td className="px-4 py-3">${r.revenue}</td>
                      </tr>
                    ))}
                  </tbody>
                  {utm.totals && (
                    <tfoot className="border-t border-[#2A3A5E] bg-[#0B132B] text-white">
                      <tr data-testid="utm-totals-row">
                        <td className="px-4 py-3 font-heading font-bold" colSpan={3}>All sources</td>
                        <td className="px-4 py-3 font-bold">{utm.totals.starts}</td>
                        <td className="px-4 py-3 font-bold">{utm.totals.purchases}</td>
                        <td className="px-4 py-3 font-bold text-[#4FD1B5]">{(utm.totals.conversion_rate * 100).toFixed(1)}%</td>
                        <td className="px-4 py-3 font-bold">${utm.totals.revenue}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </div>

        <div className="mt-10 rounded-2xl border border-[#1E2A47] bg-[#111A30] p-6" data-testid="ab-table">
          <h2 className="font-heading text-lg font-extrabold text-white">Exit popup A/B test</h2>
          {data.ab_variants.length === 0 ? (
            <p className="mt-4 text-sm text-[#7BA8C4]">No popup data yet.</p>
          ) : (
            <div className="mt-4 overflow-hidden rounded-xl border border-[#1E2A47]">
              <table className="w-full text-left text-sm">
                <thead className="bg-[#0B132B] text-[#7BA8C4]">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Variant</th>
                    <th className="px-4 py-3 font-semibold">Shown</th>
                    <th className="px-4 py-3 font-semibold">Signups</th>
                    <th className="px-4 py-3 font-semibold">Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E2A47] text-white">
                  {data.ab_variants.map((v) => (
                    <tr key={v.variant} data-testid={`ab-row-${v.variant}`}>
                      <td className="px-4 py-3 font-heading font-bold">{v.variant}</td>
                      <td className="px-4 py-3">{v.impressions}</td>
                      <td className="px-4 py-3">{v.conversions}</td>
                      <td className="px-4 py-3 font-semibold text-[#4FD1B5]">{(v.conversion_rate * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-4 text-xs text-[#5A7A94]">Variant A: plain free planner. Variant B: planner plus bonus.</p>
        </div>

        <div className="mt-6 rounded-2xl border border-[#1E2A47] bg-[#111A30] p-6" data-testid="trends-chart">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-heading text-lg font-extrabold text-white">
              Signups and revenue, last {data.range_days || range} days
            </h2>
            <div className="inline-flex rounded-lg border border-[#2A3A5E] p-0.5" data-testid="range-toggle">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => changeRange(d)}
                  data-testid={`range-${d}`}
                  data-active={range === d}
                  className={`rounded-md px-3 py-1.5 text-xs font-bold transition-colors ${
                    range === d ? "bg-[#F5A623] text-[#0B132B]" : "text-[#A8D5F5] hover:bg-white/5"
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
          <div className="mt-4 h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={(data.daily || []).map((d) => ({ ...d, day: fmtDay(d.date) }))} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid stroke="#1E2A47" vertical={false} />
                <XAxis dataKey="day" stroke="#7BA8C4" tick={{ fontSize: 11 }} interval={range === 7 ? 0 : range === 30 ? 4 : 11} tickLine={false} />
                <YAxis yAxisId="left" stroke="#4FD1B5" tick={{ fontSize: 11 }} allowDecimals={false} tickLine={false} axisLine={false} />
                <YAxis yAxisId="right" orientation="right" stroke="#F5A623" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: "#0B132B", border: "1px solid #1E2A47", borderRadius: 12, color: "#fff" }}
                  formatter={(value, name) => (name === "Revenue" ? [`$${value}`, "Revenue"] : [value, "Signups"])}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="left" dataKey="signups" name="Signups" fill="#4FD1B5" radius={[3, 3, 0, 0]} maxBarSize={18} />
                <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#F5A623" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-[#1E2A47] bg-[#111A30] p-6" data-testid="nudge-ab-table">
          <h2 className="font-heading text-lg font-extrabold text-white">Welcome email A/B (subject lines)</h2>
          {(!data.nudge_ab || data.nudge_ab.length === 0) ? (
            <p className="mt-4 text-sm text-[#7BA8C4]">No subscriber data yet.</p>
          ) : (
            <div className="mt-4 overflow-hidden rounded-xl border border-[#1E2A47]">
              <table className="w-full text-left text-sm">
                <thead className="bg-[#0B132B] text-[#7BA8C4]">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Variant</th>
                    <th className="px-4 py-3 font-semibold">Subscribers</th>
                    <th className="px-4 py-3 font-semibold">Upgrades</th>
                    <th className="px-4 py-3 font-semibold">Upgrade rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E2A47] text-white">
                  {data.nudge_ab.map((v) => (
                    <tr key={v.variant} data-testid={`nudge-row-${v.variant}`}>
                      <td className="px-4 py-3 font-heading font-bold">{v.variant}</td>
                      <td className="px-4 py-3">{v.assigned}</td>
                      <td className="px-4 py-3">{v.upgrades}</td>
                      <td className="px-4 py-3 font-semibold text-[#4FD1B5]">{(v.upgrade_rate * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="mt-4 text-xs text-[#5A7A94]">Each subscriber is locked to one subject-line set (X or Y) across the 3 nudge emails. Upgrade = a paid purchase from that subscriber.</p>
        </div>
      </div>
    </div>
  );
}
